import { useState, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Upload, FileCheck2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";

interface SupportDocumentUploadModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  embarqueId: string;
  orgId: string;
  codigosPartida: string[];
  onUploaded: () => void;
}

export function SupportDocumentUploadModal({
  open,
  onOpenChange,
  embarqueId,
  orgId,
  codigosPartida,
  onUploaded,
}: SupportDocumentUploadModalProps) {
  const { user } = useAuth();
  const { toast } = useToast();
  const fileRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);

  const handleUpload = async () => {
    if (!file || !user) return;
    setUploading(true);
    try {
      const safeName = `${user.id}_cert_${Date.now()}_${file.name.replace(/[^a-zA-Z0-9._-]/g, "_")}`;

      const { error: storageErr } = await supabase.storage
        .from("documentos_kargo")
        .upload(safeName, file, { upsert: false });

      if (storageErr) throw storageErr;

      const { error: dbErr } = await supabase.from("registro_documentos").insert({
        no_documento: `CERT-${codigosPartida[0] ?? "SOPORTE"}-${Date.now()}`,
        tipo_documento: "Certificado Técnico",
        org_id: orgId,
        user_id: user.id,
        embarque_id: embarqueId,
        archivo_url: safeName,
        estado: "Completado",
        notas_tecnicas: `Certificado soporte para partidas: ${codigosPartida.join(", ")}`,
      });

      if (dbErr) throw dbErr;

      toast({ title: "Certificado subido", description: "El documento de soporte fue registrado exitosamente." });
      setFile(null);
      if (fileRef.current) fileRef.current.value = "";
      onUploaded();
      onOpenChange(false);
    } catch (err: any) {
      toast({ title: "Error al subir", description: err.message, variant: "destructive" });
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-sm">
            <FileCheck2 className="h-4 w-4 text-primary" />
            Subir Certificado de Soporte
          </DialogTitle>
          <DialogDescription className="text-xs">
            Adjunte el certificado técnico o visto bueno requerido para las partidas:{" "}
            <span className="font-mono font-semibold">{codigosPartida.join(", ")}</span>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 pt-2">
          <div>
            <Label htmlFor="cert-file" className="text-xs">Archivo (PDF, imagen)</Label>
            <Input
              ref={fileRef}
              id="cert-file"
              type="file"
              accept=".pdf,.jpg,.jpeg,.png,.webp"
              className="mt-1 text-xs"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            />
          </div>

          <Button
            onClick={handleUpload}
            disabled={!file || uploading}
            className="w-full"
            size="sm"
          >
            {uploading ? (
              <>
                <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                Subiendo…
              </>
            ) : (
              <>
                <Upload className="h-3.5 w-3.5 mr-1.5" />
                Subir Certificado
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
